import React, { useState } from 'react';
import { INotificationData } from '../MobileAppTypes';
import { NotificationController } from './NotificationController';
import Button from '../../_Core/Button';

interface INotificationFormProps {
  appId: string;
  onNotificationCreated: () => void;
  onCancel?: () => void;
}

interface INotificationFormData {
  title: string;
  body: string;
  priority: 'low' | 'normal' | 'high';
  isScheduled: boolean;
  scheduledForUtc: string;
}

const NotificationForm: React.FC<INotificationFormProps> = ({ appId, onNotificationCreated, onCancel }) => {
  const [formData, setFormData] = useState<INotificationFormData>({
    title: '',
    body: '',
    priority: 'normal',
    isScheduled: false,
    scheduledForUtc: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (field: keyof INotificationFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.body.trim()) {
      newErrors.body = 'Message body is required';
    }

    if (formData.isScheduled) {
      if (!formData.scheduledForUtc) {
        newErrors.scheduledForUtc = 'Scheduled date and time is required';
      } else {
        const scheduledDate = new Date(formData.scheduledForUtc);
        const now = new Date();
        if (scheduledDate <= now) {
          newErrors.scheduledForUtc = 'Scheduled date must be in the future';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const notificationData: INotificationData = {
        Type: 'Basic',
        Scheduled: formData.isScheduled,
        ScheduledForUtc: formData.isScheduled ? formData.scheduledForUtc : undefined,
        Sent: false,
        Data: {
          title: formData.title.trim(),
          body: formData.body.trim(),
          priority: formData.priority,
          appId: appId,
        },
      };

      const response = await NotificationController.scheduleNotification(appId, notificationData);

      if (response.Success) {
        // Reset form
        setFormData({
          title: '',
          body: '',
          priority: 'normal',
          isScheduled: false,
          scheduledForUtc: '',
        });

        // Notify parent component
        onNotificationCreated();
      }
    } catch (error) {
      console.error('Error creating notification:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Reset form
    setFormData({
      title: '',
      body: '',
      priority: 'normal',
      isScheduled: false,
      scheduledForUtc: '',
    });
    setErrors({});

    if (onCancel) {
      onCancel();
    }
  };

  const getMinDateTime = (): string => {
    // Return current datetime + 5 minutes in local timezone
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5);

    // Format to YYYY-MM-DDTHH:mm (HTML datetime-local format)
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  return (
    <div className="form-horizontal">
      {/* <h3>Create New Notification</h3> */}

      <div className="form-group">
        <label className="control-label col-sm-4" htmlFor="notification-title">
          Title *
        </label>
        <div className="col-sm-8">
          <input
            id="notification-title"
            type="text"
            className="form-control"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="Enter notification title"
            maxLength={100}
            disabled={isSubmitting}
          />
          {errors.title && <div className="alert alert-danger">{errors.title}</div>}
        </div>
      </div>

      <div className="form-group">
        <label className="control-label col-sm-4" htmlFor="notification-body">
          Message *
        </label>
        <div className="col-sm-8">
          <textarea
            id="notification-body"
            className="form-control"
            value={formData.body}
            onChange={(e) => handleInputChange('body', e.target.value)}
            placeholder="Enter notification message"
            maxLength={500}
            disabled={isSubmitting}
          />
          <div className="small text-muted">{formData.body.length}/500 characters</div>
          {errors.body && <div className="alert alert-danger">{errors.body}</div>}
        </div>
      </div>

      <div className="form-group">
        <label className="control-label col-sm-4" htmlFor="notification-priority">
          Priority
        </label>
        <div className="col-sm-8">
          <select id="notification-priority" className="form-control" value={formData.priority} onChange={(e) => handleInputChange('priority', e.target.value)} disabled={isSubmitting}>
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
          </select>
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-offset-4 col-sm-8">
          <label className="control-label" htmlFor="notification-isScheduled">
            <input
              id="notification-isScheduled"
              type="checkbox"
              className=""
              checked={formData.isScheduled}
              onChange={(e) => handleInputChange('isScheduled', e.target.checked)}
              disabled={isSubmitting}
            />
            Schedule for later
          </label>
          <div className="small text-muted">
            {formData.isScheduled ? 'Notification will be sent at the specified time' : 'Notification will be sent immediately'}
          </div>
        </div>
      </div>

      {formData.isScheduled && (
        <div className="form-group">
          <label className="control-label col-sm-4" htmlFor="notification-scheduledForUtc">
            Scheduled Date & Time *
          </label>
          <div className="col-sm-8">
            <input
              id="notification-scheduledForUtc"
              type="datetime-local"
              className="form-control"
              value={formData.scheduledForUtc}
              onChange={(e) => handleInputChange('scheduledForUtc', e.target.value)}
              min={getMinDateTime()}
              disabled={isSubmitting}
            />
            <small className="help-block text-muted">Time is in the fair's local time zone.</small>
            {errors.scheduledForUtc && <div className="alert alert-danger">{errors.scheduledForUtc}</div>}
          </div>
        </div>
      )}

      <div className="form-group">
        <div className="col-sm-offset-4 col-sm-8 btn-group">
          <Button type="cancel" onClick={handleCancel} disabled={isSubmitting} text="Cancel" />
          <Button
            type="save"
            onClick={() => {
              handleSubmit();
            }}
            disabled={isSubmitting}
            text="Save"
          />
        </div>
      </div>
    </div>
  );
};

export default NotificationForm;
